import React, { useState, useEffect } from 'react';
import axios from 'axios';
import backendURL from '../../config';
import {
  Box, Typography, Button, CircularProgress, Modal, Alert, TextField,
  Collapse, IconButton, FormControl, InputLabel, Select, MenuItem, Grid,
  Card, CardContent,  Chip, List, ListItem, ListItemText, Divider
} from '@mui/material';
import AddLinkIcon from '@mui/icons-material/AddLink';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

const BalanceDisplay = ({ onWithdrawalSuccess, refreshKey }) => {
    const [balance, setBalance] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAccountReady, setIsAccountReady] = useState(false);
    const [error, setError] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [withdrawalAmount, setWithdrawalAmount] = useState('');

    // --- NEW STATE for the collapsible link generation form ---
    const [isLinkFormOpen, setLinkFormOpen] = useState(false);
    const [linkRequest, setLinkRequest] = useState({
        amount: '',
        title: '', // Default title
        description: '',
        payment_type: 'others' // Default type
    });
    const [planRatios, setPlanRatios] = useState({
        all_plan_ratios: [],
        current_user_plan: null
    });
    const [isRatiosLoading, setIsRatiosLoading] = useState(true);

    const [generatedLink, setGeneratedLink] = useState('');
    const [isGeneratingLink, setIsGeneratingLink] = useState(false);
    const [linkError, setLinkError] = useState('');

    const token = localStorage.getItem("access_token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        setIsLoading(true);
        axios.get(`${backendURL}/payments/balance`, { headers })
            .then(res => {
                setBalance(res.data.balance);
                setIsAccountReady(res.data.is_stripe_account_ready);
            })
            .catch(err => setError('Failed to load balance.'))
            .finally(() => setIsLoading(false));

        // --- NEW: Fetch plan ratio data ---
        setIsRatiosLoading(true);
        axios.get(`${backendURL}/payments/plan-ratios`, { headers: { Authorization: `Bearer ${token}` }})
            .then(res => {
                setPlanRatios(res.data.data);
            })
            .catch(err => { if (!axios.isCancel(err)) console.error("Failed to fetch plan ratios", err); })
            .finally(() => setIsRatiosLoading(false));
    
    }, [refreshKey]);

    const handleConnectStripe = async () => {
        setIsProcessing(true);
        try {
            const res = await axios.post(`${backendURL}/payments/create-connect-link`, {
                return_url: window.location.href,
                refresh_url: window.location.href,
            }, { headers });
            window.location.href = res.data.url;
        } catch (err) {
            setError(err.response?.data?.message || 'Could not create Stripe connection link.');
            setIsProcessing(false);
        }
    };
    
    // --- UPDATED to send the specific withdrawal amount ---
    const handleWithdraw = async () => {
        setIsProcessing(true);
        setError('');
        try {
            // Send the amount from the state, not the total balance
            await axios.post(`${backendURL}/payments/initiate-withdrawal`, { amount: withdrawalAmount }, { headers });
            setOpenModal(false);
            onWithdrawalSuccess(); // Refresh data on parent component
        } catch (err) {
            setError(err.response?.data?.message || 'Withdrawal failed.');
        } finally {
            setIsProcessing(false);
        }
    };

    // --- NEW function to handle opening the modal ---
    const handleOpenModal = () => {
        // Pre-fill the input with the full balance for convenience
        setWithdrawalAmount(balance);
        setError(''); // Clear any previous errors
        setOpenModal(true);
    };


    // --- NEW: Handle changes in the link generation form ---
    const handleLinkRequestChange = (e) => {
        const { name, value } = e.target;
        setLinkRequest(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    // --- NEW: API call to generate the specific payment link ---
    const handleGenerateLink = async (e) => {
        e.preventDefault();
        setIsGeneratingLink(true);
        setGeneratedLink('');
        setLinkError('');
        try {
            const res = await axios.post(`${backendURL}/payments/generate-specific-payment-token`, linkRequest, { headers });
            setGeneratedLink(`${window.location.origin}/pay/${res.data.token}`);
        } catch (err) {
            setLinkError(err.response?.data?.message || 'Failed to generate link.');
        } finally {
            setIsGeneratingLink(false);
        }
    };
    
    // --- NEW: Handler to copy the generated link ---
    const handleCopyLink = () => {
        navigator.clipboard.writeText(generatedLink).then(() => {
            alert('Payment link copied to clipboard!');
        });
    };

    if (isLoading) return <CircularProgress />;

    const isWithdrawalAmountInvalid = !withdrawalAmount || parseFloat(withdrawalAmount) <= 0 || parseFloat(withdrawalAmount) > parseFloat(balance);
    const isGenerateLinkDisabled = isGeneratingLink || !linkRequest.amount || !linkRequest.title;

    return (
        <Box>
            <Typography variant="h6">Current Available Balance</Typography>
            <Typography variant="h3" color="primary">{balance ? `$${Number(balance).toFixed(2)}` : '$0.00'}</Typography>
            {error && !openModal && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button 
                    variant="contained" 
                    onClick={handleOpenModal}
                    disabled={!balance || parseFloat(balance) <= 0}
                >
                    Withdraw Funds
                </Button>
                {/* --- NEW: Button to toggle the collapsible form --- */}
                <Button 
                    variant="outlined"
                    startIcon={<AddLinkIcon />}
                    onClick={() => setLinkFormOpen(!isLinkFormOpen)}
                >
                    Create Payment Link
                </Button>
            </Box>

            {/* --- NEW: Collapsible Form Section --- */}
            <Collapse in={isLinkFormOpen}>
                <Box component="form" onSubmit={handleGenerateLink} sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                    <Typography variant="h6" gutterBottom>Create a Specific Link</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel id="payment-type-label">Payment Type</InputLabel>
                                <Select
                                    labelId="payment-type-label"
                                    name="payment_type"
                                    value={linkRequest.payment_type}
                                    label="Payment Type"
                                    onChange={handleLinkRequestChange}
                                >
                                    <MenuItem value="donation">Donation</MenuItem>
                                    <MenuItem value="signup">Signup</MenuItem>
                                    <MenuItem value="others">Others</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth required label="Amount (USD)" name="amount" type="number" value={linkRequest.amount} onChange={handleLinkRequestChange} inputProps={{ step: "0.01", min: "0.50" }} />
                        </Grid>
                        <Grid item xs={12}>
                             <TextField fullWidth required label="Payment Title" name="title" value={linkRequest.title} onChange={handleLinkRequestChange} helperText={'Enter a custom title'} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Description (Optional)" name="description" multiline rows={2} value={linkRequest.description} onChange={handleLinkRequestChange} />
                        </Grid>
                    </Grid>
                    <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={isGenerateLinkDisabled}>
                        {isGeneratingLink ? <CircularProgress size={24} /> : 'Generate Link'}
                    </Button>
                    {linkError && <Alert severity="error" sx={{ mt: 2 }}>{linkError}</Alert>}
                    {generatedLink && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2">Link Generated:</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <TextField fullWidth value={generatedLink} InputProps={{ readOnly: true }} size="small"/>
                                <IconButton onClick={handleCopyLink}><ContentCopyIcon /></IconButton>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Collapse>

            
            {/* --- NEW: Plan Ratio Information Section --- */}
            <Grid container spacing={3} sx={{ mt: 2 }}>
                {/* Current Plan Card */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Your Current Plan</Typography>
                            {isRatiosLoading ? <CircularProgress size={24} /> : (
                                planRatios.current_user_plan ? (
                                    <Box>
                                        <Chip label={planRatios.current_user_plan.plan_name} color="primary" sx={{ mb: 1 }} />
                                        <Typography variant="body1">
                                            Your revenue share for each payment is:
                                        </Typography>
                                        <Typography variant="h4" component="p" color="secondary.main" sx={{ fontWeight: 'bold' }}>
                                            {planRatios.current_user_plan.user_share}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Typography>No active plan found.</Typography>
                                )
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                {/* All Plans Card */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Platform Revenue Share by Plan</Typography>
                            {isRatiosLoading ? <CircularProgress size={24} /> : (
                                <List dense>
                                    {planRatios.all_plan_ratios.map((plan, index) => (
                                        <React.Fragment key={plan.plan_name}>
                                            <ListItem>
                                                <ListItemText 
                                                    primary={plan.plan_name} 
                                                    secondary={`User receives ${plan.user_share} of each payment`}
                                                />
                                            </ListItem>
                                            {index < planRatios.all_plan_ratios.length - 1 && <Divider component="li" />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
                

            <Modal open={openModal} onClose={() => setOpenModal(false)}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" component="h2">Initiate Withdrawal</Typography>
                    
                    {/* --- REPLACED static text with a dynamic TextField --- */}
                    <TextField
                        fullWidth
                        required
                        label="Amount to Withdraw"
                        type="number"
                        margin="normal"
                        value={withdrawalAmount}
                        onChange={(e) => setWithdrawalAmount(e.target.value)}
                        InputProps={{
                            startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                        }}
                        error={isWithdrawalAmountInvalid && withdrawalAmount !== ''}
                        helperText={
                            parseFloat(withdrawalAmount) > parseFloat(balance)
                                ? "Cannot withdraw more than your available balance."
                                : ""
                        }
                    />

                    {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

                    {isAccountReady ? (
                        <Button 
                            onClick={handleWithdraw} 
                            // --- UPDATED disabled logic for validation ---
                            disabled={isProcessing || isWithdrawalAmountInvalid} 
                            fullWidth 
                            variant="contained" 
                            sx={{ mt: 3 }}
                        >
                            {isProcessing ? <CircularProgress size={24} /> : `Confirm and Withdraw $${withdrawalAmount}`}
                        </Button>
                    ) : (
                        <>
                            <Alert severity="warning" sx={{mt: 2}}>Your Stripe account is not ready for withdrawals. Please complete your setup.</Alert>
                            <Button onClick={handleConnectStripe} disabled={isProcessing} fullWidth variant="contained" color="secondary" sx={{ mt: 3 }}>
                                {isProcessing ? <CircularProgress size={24} /> : 'Connect & Set Up Stripe Account'}
                            </Button>
                        </>
                    )}
                </Box>
            </Modal>
        </Box>
    );
};

export default BalanceDisplay;